import { useCallback, useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cn } from '@/lib/utils';

type Side = 'left' | 'right' | 'bottom' | 'top';

export interface TruncatedTextProps {
  /**
   * 提示内容（tooltip）
   *
   * 文本超出被省略时展示的完整内容。传入 `children` 时以 `content` 为提示、
   * `children` 为展示内容，两者不一致的典型场景是「展示省略版、提示完整版」；
   * 未传 `content` 时回退用 `children` 作为提示。
   */
  content?: ReactNode;
  /**
   * 展示内容
   *
   * 未传时展示 `content`；传入后由它接管展示（`content` 只作为提示）。
   */
  children?: ReactNode;
  /**
   * 最大显示行数
   */
  maxLines?: number;
  /**
   * 指定宽度
   */
  width?: number | string;
  /**
   * 提示 tooltip 最大宽度
   */
  tooltipMaxWidth?: number | string;
  className?: string;
  /**
   * TooltipContent 的 className
   */
  tooltipClassName?: string;
  /**
   * TooltipContent 的弹出方向
   */
  tooltipSide?: Side;
  onClick?: () => void;
}

/**
 * 超出即省略、悬停展示完整内容的文本容器
 *
 * - 展示内容：优先 `children`，未传时用 `content`
 * - 提示内容：优先 `content`，未传时回退 `children`
 * - 用 `-webkit-line-clamp` 做纵向裁剪，靠 `scrollHeight > clientHeight` 判断是否被截断；
 *   因此**不要**在外层再加 `truncate`（`white-space: nowrap` 只会横向溢出，高度不变会导致判定失效）
 */
export function TruncatedText({
  content,
  children,
  maxLines = 1,
  width,
  tooltipMaxWidth = 300,
  className,
  tooltipClassName,
  tooltipSide,
  onClick,
}: TruncatedTextProps) {
  const textRef = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const checkTruncation = useCallback(() => {
    const el = textRef.current;
    if (!el) return;

    // line-clamp 会把视觉高度固定住，滚动高度才是内容的真实高度
    const isOverflowing = el.scrollHeight > el.clientHeight + 1;

    setIsTruncated(isOverflowing);
  }, []);

  /**
   * 每次提交后重新检测
   *
   * 容器高度被 line-clamp 固定后，内容变长并不会改变容器尺寸，ResizeObserver 不会触发；
   * 而 `children` / `content` 是 ReactNode，每次渲染都是新元素，无法作为稳定依赖，
   * 因此改为「每次提交都检测」——无依赖数组，天然覆盖内容与尺寸两种变化。
   */
  useEffect(() => {
    // 双 rAF 确保样式完全应用后再检测
    let rafId = requestAnimationFrame(() => {
      rafId = requestAnimationFrame(checkTruncation);
    });

    return () => cancelAnimationFrame(rafId);
  });

  /**
   * 截断态会切换根节点（包/不包 Tooltip 触发器），节点重建后需要重新绑定观察者
   * 因此以 `isTruncated` 为依赖重新执行
   */
  useEffect(() => {
    const el = textRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(checkTruncation);
    observer.observe(el);

    return () => observer.disconnect();
  }, [checkTruncation, isTruncated]);

  const style: CSSProperties = {};
  if (width !== undefined) {
    style.width = typeof width === 'number' ? `${width}px` : width;
  }

  /** 展示内容优先 children，其次 content */
  const displayContent = children ?? content;
  /** 提示内容优先 content，其次 children */
  const tooltipContent = content ?? children;

  const textElement = (
    <div
      ref={textRef}
      className={cn('block min-w-0 break-all whitespace-normal', className)}
      style={{
        ...style,
        display: '-webkit-box',
        WebkitLineClamp: maxLines,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
      }}
      onClick={onClick}
    >
      {displayContent}
    </div>
  );

  if (!isTruncated) {
    return textElement;
  }

  return (
    <TooltipPrimitive.Provider delayDuration={300}>
      <TooltipPrimitive.Root>
        <TooltipPrimitive.Trigger asChild>{textElement}</TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal>
          <TooltipPrimitive.Content
            side={tooltipSide || 'top'}
            align="start"
            sideOffset={6}
            className={cn(
              'z-50 rounded-md bg-zinc-900 px-3 py-2 text-sm text-zinc-50 shadow-md break-all',
              'animate-in fade-in-0 zoom-in-95',
              'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
              'dark:bg-zinc-50 dark:text-zinc-900',
              tooltipClassName
            )}
            style={{
              maxWidth:
                typeof tooltipMaxWidth === 'number' ? `${tooltipMaxWidth}px` : tooltipMaxWidth,
            }}
          >
            {tooltipContent}
            <TooltipPrimitive.Arrow className="fill-zinc-900 dark:fill-zinc-50" />
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  );
}

